import { Clock } from 'lucide-react';
import { formatDate } from '@/lib/formatters';

/**
 * Compose a normalized list of activity events for an application.
 * Consumed by <ActivityTimelineCard/> below. Newest-first.
 *
 * @param {object} app  application document
 * @param {array}  offers    offers on the application (may be [] for funder view)
 * @param {array}  docRequests document_requests rows
 */
export const buildTimelineEvents = (app, offers, docRequests) => {
  const events = [];
  if (app.created_at) {
    events.push({ key: 'created', label: 'Application created', detail: 'Draft started', ts: app.created_at, color: '#64748b' });
  }
  if (app.submitted_at) {
    events.push({ key: 'submitted', label: 'Submitted for lawyer review', detail: app.lawFirmName ? `Routed to ${app.lawFirmName}` : 'Awaiting attorney validation', ts: app.submitted_at, color: '#2563eb' });
  }
  if (app.validated_at) {
    events.push({ key: 'validated', label: 'Validated by lawyer', detail: app.validated_by_name ? `By ${app.validated_by_name}` : 'Released to funders', ts: app.validated_at, color: '#16a34a' });
  }
  if (app.declined_at) {
    events.push({ key: 'declined', label: 'Declined by lawyer', detail: app.client_feedback ? `Feedback: ${app.client_feedback}` : '', ts: app.declined_at, color: '#dc2626' });
  }
  (offers || []).forEach((o) => {
    const funder = o.funder_company || o.funder_name || 'Funder';
    if (o.submitted_at) {
      const amt = o.advance_amount ? ` · $${parseFloat(o.advance_amount).toLocaleString()}` : '';
      events.push({ key: `offer-${o.offer_id}-submitted`, label: `Offer submitted by ${funder}`, detail: `${(o.status || '').replace(/_/g, ' ')}${amt}`, ts: o.submitted_at, color: '#7c3aed' });
    }
    if (o.accepted_at) {
      events.push({ key: `offer-${o.offer_id}-accepted`, label: `Offer accepted from ${funder}`, detail: 'Funding in progress', ts: o.accepted_at, color: '#0d9488' });
    }
    if (o.declined_at) {
      events.push({ key: `offer-${o.offer_id}-declined`, label: `Offer declined from ${funder}`, detail: '', ts: o.declined_at, color: '#9ca3af' });
    }
  });
  // Document requests — one event when the funder created it, another when it
  // was fulfilled by whoever uploaded the file.
  (docRequests || []).forEach((r) => {
    const funder = r.funder_company || r.funder_name || 'Funder';
    const label = r.display_label || r.document_category || 'Document';
    if (r.requested_at) {
      events.push({
        key: `docreq-${r.request_id}-requested`,
        label: `Document requested by ${funder}`,
        detail: r.notes ? `${label} · ${r.notes}` : label,
        ts: r.requested_at,
        color: '#d97706',
      });
    }
    if (r.fulfilled_at || (r.status === 'fulfilled' && r.uploaded_at)) {
      const uploader = r.uploaded_by_name || 'a team member';
      events.push({
        key: `docreq-${r.request_id}-fulfilled`,
        label: `Document uploaded for ${funder}`,
        detail: `${label} · by ${uploader}`,
        ts: r.fulfilled_at || r.uploaded_at,
        color: '#16a34a',
      });
    }
  });
  if (app.approved_at) {
    events.push({ key: 'approved', label: 'Application approved', detail: 'Marketplace listed', ts: app.approved_at, color: '#0ea5e9' });
  }
  if (app.funded_at) {
    events.push({ key: 'funded', label: 'Funded', detail: '', ts: app.funded_at, color: '#16a34a' });
  }
  return events
    .filter(e => !!e.ts)
    .sort((a, b) => String(b.ts).localeCompare(String(a.ts)));
};

/**
 * Renders the newest-first Activity Timeline card. Shared across
 * Lawyer / Client / Funder application detail views for a uniform experience.
 */
export const ActivityTimelineCard = ({ application, offers, documentRequests }) => {
  const events = buildTimelineEvents(application, offers, documentRequests);
  return (
    <div
      className="mcl-card mb-3"
      data-testid="activity-timeline-card"
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
    >
      <div
        className="mcl-card-header"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0.95rem' }}
      >
        <h3 className="mcl-card-header-title" style={{ fontSize: '0.85rem', margin: 0 }}>Activity Timeline</h3>
        <Clock size={14} style={{ color: 'var(--mcl-text-muted)' }} />
      </div>
      <div
        data-testid="activity-timeline-list"
        style={{ padding: '0.75rem 0.95rem', maxHeight: '320px', overflowY: 'auto', flex: 1 }}
      >
        {events.length === 0 ? (
          <div style={{ fontSize: '0.78rem', color: 'var(--mcl-text-muted)', padding: '0.4rem 0' }}>
            No activity yet.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {events.map((e, idx) => (
              <li
                key={e.key}
                data-testid={`activity-event-${e.key}`}
                style={{ position: 'relative', paddingLeft: '1.05rem', paddingBottom: idx === events.length - 1 ? 0 : '0.75rem' }}
              >
                <span
                  aria-hidden
                  style={{
                    position: 'absolute', left: '2px', top: '5px',
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: e.color, boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${e.color}33`,
                  }}
                />
                {idx !== events.length - 1 && (
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute', left: '5px', top: '15px',
                      width: '2px', bottom: 0, background: '#e2e8f0',
                    }}
                  />
                )}
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--mcl-text-heading)', lineHeight: 1.3 }}>{e.label}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', marginTop: '1px' }}>{formatDate(e.ts)}</div>
                {e.detail && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--mcl-text-body)', marginTop: '2px', wordBreak: 'break-word' }}>{e.detail}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ActivityTimelineCard;
